import React from 'react';
import { FaTrash, FaArrowUp, FaArrowDown, FaPlus, FaChartLine } from 'react-icons/fa';

export const FIELD_TYPES = [
  { value: 'number', label: 'Number' },
  { value: 'text', label: 'Short text' },
  { value: 'textarea', label: 'Long text' },
  { value: 'select', label: 'Choice list' },
  { value: 'boolean', label: 'Yes / No' },
  { value: 'date', label: 'Date' },
  { value: 'time', label: 'Time' },
];

export const FREQUENCIES = ['Daily', 'Twice daily', 'Weekly', 'Fortnightly', 'Monthly', 'As advised'];

// Starting points so a doctor rarely builds a common form from nothing.
export const STARTER_FIELDS = [
  { label: 'Systolic BP', type: 'number', unit: 'mmHg', normalLow: 90, normalHigh: 120, chart: true },
  { label: 'Diastolic BP', type: 'number', unit: 'mmHg', normalLow: 60, normalHigh: 80, chart: true },
  { label: 'Pulse', type: 'number', unit: 'bpm', normalLow: 60, normalHigh: 100, chart: true },
  { label: 'Temperature', type: 'number', unit: '°C', normalLow: 36.5, normalHigh: 37.5, chart: true },
  { label: 'SpO₂', type: 'number', unit: '%', normalLow: 95, normalHigh: 100, chart: true },
  { label: 'Respiratory rate', type: 'number', unit: '/min', normalLow: 12, normalHigh: 20, chart: true },
  { label: 'Weight', type: 'number', unit: 'kg', chart: true },
  { label: 'Blood glucose', type: 'number', unit: 'mg/dL', normalLow: 70, normalHigh: 140, chart: true },
  { label: 'Pain score', type: 'number', unit: '0-10', min: 0, max: 10, chart: true },
];

export const emptyField = (overrides = {}) => ({
  key: '',
  label: '',
  type: 'number',
  unit: '',
  helpText: '',
  options: [],
  required: false,
  min: '',
  max: '',
  normalLow: '',
  normalHigh: '',
  chart: true,
  ...overrides,
});

const labelCls = 'block text-[11px] font-semibold uppercase tracking-wide text-slate-500';
const inputCls =
  'mt-1 w-full rounded-md border border-slate-300 px-2.5 py-1.5 text-sm text-slate-800 focus:border-primary-400 focus:outline-none focus:ring-1 focus:ring-primary-400';

/** One field's editor row. */
const FieldRow = ({ field, index, total, onChange, onMove, onRemove }) => {
  const set = (patch) => onChange(index, { ...field, ...patch });
  const isNumber = field.type === 'number';

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex items-start gap-3">
        <span className="mt-2 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-slate-100 text-xs font-semibold text-slate-500">
          {index + 1}
        </span>

        <div className="grid flex-1 grid-cols-1 gap-3 md:grid-cols-12">
          <div className="md:col-span-5">
            <label className={labelCls}>Question / field label</label>
            <input
              className={inputCls}
              value={field.label}
              placeholder="e.g. Systolic BP"
              onChange={(e) => set({ label: e.target.value })}
            />
          </div>

          <div className="md:col-span-3">
            <label className={labelCls}>Type</label>
            <select className={inputCls} value={field.type} onChange={(e) => set({ type: e.target.value })}>
              {FIELD_TYPES.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
          </div>

          <div className="md:col-span-2">
            <label className={labelCls}>Unit</label>
            <input
              className={inputCls}
              value={field.unit}
              placeholder="mmHg"
              onChange={(e) => set({ unit: e.target.value })}
            />
          </div>

          <div className="flex items-end gap-3 md:col-span-2">
            <label className="flex items-center gap-1.5 pb-1.5 text-xs font-medium text-slate-600">
              <input type="checkbox" checked={!!field.required} onChange={(e) => set({ required: e.target.checked })} />
              Required
            </label>
          </div>

          {isNumber && (
            <>
              <div className="md:col-span-2">
                <label className={labelCls}>Min allowed</label>
                <input type="number" className={inputCls} value={field.min ?? ''} onChange={(e) => set({ min: e.target.value })} />
              </div>
              <div className="md:col-span-2">
                <label className={labelCls}>Max allowed</label>
                <input type="number" className={inputCls} value={field.max ?? ''} onChange={(e) => set({ max: e.target.value })} />
              </div>
              <div className="md:col-span-2">
                <label className={labelCls}>Normal from</label>
                <input type="number" className={inputCls} value={field.normalLow ?? ''} onChange={(e) => set({ normalLow: e.target.value })} />
              </div>
              <div className="md:col-span-2">
                <label className={labelCls}>Normal to</label>
                <input type="number" className={inputCls} value={field.normalHigh ?? ''} onChange={(e) => set({ normalHigh: e.target.value })} />
              </div>
              <div className="flex items-end md:col-span-4">
                <label className="flex items-center gap-1.5 pb-1.5 text-xs font-medium text-slate-600">
                  <input type="checkbox" checked={field.chart !== false} onChange={(e) => set({ chart: e.target.checked })} />
                  <FaChartLine className="text-primary-500" /> Plot on the trend graph
                </label>
              </div>
            </>
          )}

          {field.type === 'select' && (
            <div className="md:col-span-12">
              <label className={labelCls}>Choices (one per line)</label>
              <textarea
                rows={3}
                className={inputCls}
                value={(field.options || []).join('\n')}
                placeholder={'Mild\nModerate\nSevere'}
                onChange={(e) => set({ options: e.target.value.split('\n') })}
              />
            </div>
          )}

          <div className="md:col-span-12">
            <label className={labelCls}>Help text shown to the patient (optional)</label>
            <input
              className={inputCls}
              value={field.helpText}
              placeholder="e.g. Measure after sitting quietly for five minutes"
              onChange={(e) => set({ helpText: e.target.value })}
            />
          </div>
        </div>

        <div className="flex shrink-0 flex-col gap-1">
          <button
            type="button"
            title="Move up"
            disabled={index === 0}
            onClick={() => onMove(index, -1)}
            className="rounded p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700 disabled:opacity-30"
          >
            <FaArrowUp />
          </button>
          <button
            type="button"
            title="Move down"
            disabled={index === total - 1}
            onClick={() => onMove(index, 1)}
            className="rounded p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700 disabled:opacity-30"
          >
            <FaArrowDown />
          </button>
          <button
            type="button"
            title="Remove field"
            onClick={() => onRemove(index)}
            className="rounded p-1.5 text-rose-400 hover:bg-rose-50 hover:text-rose-600"
          >
            <FaTrash />
          </button>
        </div>
      </div>
    </div>
  );
};

/**
 * The form-builder body. Kept separate from the page so it can be dropped into
 * a modal elsewhere without dragging the list view along.
 */
export default function VitalTemplateBuilder({ draft, setDraft }) {
  const setField = (index, next) =>
    setDraft((d) => ({ ...d, fields: d.fields.map((f, i) => (i === index ? next : f)) }));

  const moveField = (index, delta) =>
    setDraft((d) => {
      const fields = [...d.fields];
      const target = index + delta;
      if (target < 0 || target >= fields.length) return d;
      [fields[index], fields[target]] = [fields[target], fields[index]];
      return { ...d, fields };
    });

  const removeField = (index) => setDraft((d) => ({ ...d, fields: d.fields.filter((_, i) => i !== index) }));

  const addField = (preset) => setDraft((d) => ({ ...d, fields: [...d.fields, emptyField(preset)] }));

  const usedLabels = new Set(draft.fields.map((f) => f.label));

  return (
    <div className="space-y-5">
      <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <div className="md:col-span-2">
            <label className={labelCls}>Template name</label>
            <input
              className={inputCls}
              value={draft.name}
              placeholder="e.g. Hypertension — daily monitoring"
              onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
            />
          </div>
          <div>
            <label className={labelCls}>Default frequency</label>
            <select
              className={inputCls}
              value={draft.frequency}
              onChange={(e) => setDraft((d) => ({ ...d, frequency: e.target.value }))}
            >
              {FREQUENCIES.map((f) => (
                <option key={f} value={f}>
                  {f}
                </option>
              ))}
            </select>
          </div>
          <div className="md:col-span-3">
            <label className={labelCls}>Description / instructions</label>
            <textarea
              rows={2}
              className={inputCls}
              value={draft.description}
              placeholder="Shown to the patient above the form"
              onChange={(e) => setDraft((d) => ({ ...d, description: e.target.value }))}
            />
          </div>
        </div>
      </div>

      <div className="space-y-3">
        {draft.fields.map((field, index) => (
          <FieldRow
            key={index}
            field={field}
            index={index}
            total={draft.fields.length}
            onChange={setField}
            onMove={moveField}
            onRemove={removeField}
          />
        ))}

        {!draft.fields.length && (
          <p className="rounded-xl border border-dashed border-slate-300 bg-white/60 p-8 text-center text-sm text-slate-500">
            No fields yet. Add a common vital sign below, or build your own.
          </p>
        )}
      </div>

      <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
        <button
          type="button"
          onClick={() => addField()}
          className="inline-flex items-center gap-2 rounded-lg bg-primary-500 px-4 py-2 text-sm font-medium text-white hover:bg-primary-600"
        >
          <FaPlus /> Add field
        </button>

        <p className="mt-4 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
          Or start from a common vital sign
        </p>
        <div className="mt-2 flex flex-wrap gap-2">
          {STARTER_FIELDS.filter((s) => !usedLabels.has(s.label)).map((starter) => (
            <button
              key={starter.label}
              type="button"
              onClick={() => addField(starter)}
              className="rounded-full border border-slate-300 bg-white px-3 py-1 text-xs font-medium text-slate-600 hover:border-primary-400 hover:text-primary-600"
            >
              + {starter.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
